import { ColumnSizing } from '../tables-context';
import { MRT_ColumnDef } from 'material-react-table';
import { TableCell } from '../../../components/table/TableCell';
import React from 'react';
import { numberFormatter, numberFormatterCount } from '../../../utils/utilities';
import { IPartnerCommissionsDetails } from '../../order/order-interfaces';
import { RELATED_ENTITY_TYPE, RelatedEntityNameLink } from '../../../components/merchants/RelatedEntityNameLink';

export enum PartnersCommissionsDetailsDataTableAccessorKeys {
	mid = 'mid',
	commissionOnGrossRevenue = 'commissionOnGrossRevenue',
	commissionOnNetRevenue = 'commissionOnNetRevenue',
	commissionOnVolume = 'commissionOnVolume',
	commissionTotal = 'commissionTotal',
	grossRevenue = 'grossRevenue',
	netRevenue = 'netRevenue',
	transactionsCount = 'transactionsCount',
}

export const defaultPartnersCommissionsDetailsColumnSizing: ColumnSizing = {
	[PartnersCommissionsDetailsDataTableAccessorKeys.mid]: 100,
	[PartnersCommissionsDetailsDataTableAccessorKeys.commissionOnGrossRevenue]: 100,
	[PartnersCommissionsDetailsDataTableAccessorKeys.commissionOnNetRevenue]: 100,
	[PartnersCommissionsDetailsDataTableAccessorKeys.commissionOnVolume]: 100,
	[PartnersCommissionsDetailsDataTableAccessorKeys.commissionTotal]: 100,
	[PartnersCommissionsDetailsDataTableAccessorKeys.grossRevenue]: 100,
	[PartnersCommissionsDetailsDataTableAccessorKeys.netRevenue]: 100,
	[PartnersCommissionsDetailsDataTableAccessorKeys.transactionsCount]: 100,
};

const getCommissionsValue = (value?: number, currencySymbol?: string): JSX.Element => {
	return (
		<div>
			{value
				?`${currencySymbol}${numberFormatter(value/100)}`
				: '-'
			}
		</div>
	);
};

export const defaultPartnersCommissionsDetailsColumns: MRT_ColumnDef<IPartnerCommissionsDetails>[] = [
	{
		accessorKey: PartnersCommissionsDetailsDataTableAccessorKeys.mid,
		header: 'Merchant name',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Merchant name</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<RelatedEntityNameLink id={renderedCellValue} type={RELATED_ENTITY_TYPE.MERCHANT} />
			</TableCell>
		)
	},
	{
		accessorKey: PartnersCommissionsDetailsDataTableAccessorKeys.commissionOnGrossRevenue,
		header: 'Commissions on gross revenue',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Commissions on gross revenue</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				{getCommissionsValue(renderedCellValue, row?.original?.currencySymbol)}
			</TableCell>
		)
	},
	{
		accessorKey: PartnersCommissionsDetailsDataTableAccessorKeys.commissionOnNetRevenue,
		header: 'Commissions on net revenue',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Commissions on net revenue</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				{getCommissionsValue(renderedCellValue, row?.original?.currencySymbol)}
			</TableCell>
		)
	},
	{
		accessorKey: PartnersCommissionsDetailsDataTableAccessorKeys.commissionOnVolume,
		header: 'Commissions on volume',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Commissions on volume</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				{renderedCellValue ? numberFormatterCount(renderedCellValue) : '-'}
			</TableCell>
		)
	},
	{
		accessorKey: PartnersCommissionsDetailsDataTableAccessorKeys.grossRevenue,
		header: 'Gross revenue',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Gross revenue</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				{renderedCellValue ? numberFormatterCount(renderedCellValue) : '-'}
			</TableCell>
		)
	},
	{
		accessorKey: PartnersCommissionsDetailsDataTableAccessorKeys.netRevenue,
		header: 'Net revenue',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Net revenue</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				{renderedCellValue ? numberFormatterCount(renderedCellValue) : '-'}
			</TableCell>
		)
	},
	{
		accessorKey: PartnersCommissionsDetailsDataTableAccessorKeys.transactionsCount,
		header: 'Transactions count',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Transactions count</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				{renderedCellValue ? numberFormatterCount(renderedCellValue) : '-'}
			</TableCell>
		)
	},
	{
		accessorKey: PartnersCommissionsDetailsDataTableAccessorKeys.commissionTotal,
		header: 'Commissions total',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Commissions total</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				{getCommissionsValue(renderedCellValue, row?.original?.currencySymbol)}
			</TableCell>
		)
	},

];

export interface IMockDetailsData {
	mid: string,
	merchantGrossRevenue: number,
	merchantNetRevenue: number,
	merchantTransactions: number,
	partnersGrossRevenue: number,
	partnersNetRevenue: number,
	partnersTransactions: number,
	partnersGrossRevenueCommissions: number,
	partnersNetRevenueCommissions: number,
	partnersTransactionsCommissions: number,
	total: number,
}

export const mockDetailsData: IMockDetailsData[] = [
	{
		mid: 'be709bee-2fc1-49a5-beb4-6ed75d8fa33b',
		merchantGrossRevenue: 175650.12,
		merchantNetRevenue: 89650.98,
		merchantTransactions: 12030,
		partnersGrossRevenue: 30,
		partnersNetRevenue: null,
		partnersTransactions: 0.03,
		partnersGrossRevenueCommissions: 1515.33,
		partnersNetRevenueCommissions: 5593.40,
		partnersTransactionsCommissions: 2753.55,
		total: 1973.12,
	},
	{
		mid: '4f9ef66c-6a33-4b8f-9a6a-4db33720b2e0',
		merchantGrossRevenue: 175650.12,
		merchantNetRevenue: 89650.98,
		merchantTransactions: 12030,
		partnersGrossRevenue: null,
		partnersNetRevenue: 18,
		partnersTransactions: null,
		partnersGrossRevenueCommissions: 1515.33,
		partnersNetRevenueCommissions: 5593.40,
		partnersTransactionsCommissions: 2753.55,
		total: 1973.12,
	},
	{
		mid: '358898d5-9025-4434-a954-d2415e61a767',
		merchantGrossRevenue: 175650.12,
		merchantNetRevenue: 89650.98,
		merchantTransactions: 12030,
		partnersGrossRevenue: null,
		partnersNetRevenue: 12,
		partnersTransactions: null,
		partnersGrossRevenueCommissions: 1515.33,
		partnersNetRevenueCommissions: 5593.40,
		partnersTransactionsCommissions: 2753.55,
		total: 1973.12,
	},
	{
		mid: '76e105a7-4d60-457b-82f2-648d46aba287',
		merchantGrossRevenue: 175650.12,
		merchantNetRevenue: 89650.98,
		merchantTransactions: 12030,
		partnersGrossRevenue: 30,
		partnersNetRevenue: null,
		partnersTransactions: 0.03,
		partnersGrossRevenueCommissions: 1515.33,
		partnersNetRevenueCommissions: 5593.40,
		partnersTransactionsCommissions: 2753.55,
		total: 1973.12,
	},
	{
		mid: 'c0b80b03-eb7a-4ebd-8418-b095c5dee20a',
		merchantGrossRevenue: 175650.12,
		merchantNetRevenue: 89650.98,
		merchantTransactions: 12030,
		partnersGrossRevenue: 30,
		partnersNetRevenue: null,
		partnersTransactions: 0.03,
		partnersGrossRevenueCommissions: 1515.33,
		partnersNetRevenueCommissions: 5593.40,
		partnersTransactionsCommissions: 2753.55,
		total: 1973.12,
	},
];